import { Fragment, useEffect, useMemo, useState } from "react";
import { buildMergedBranchAssignmentReportMap } from "../../../utilization/ReportDetailBuilder";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/UserContextProvider";
import {
  ESTIMATED_SCORE_MAX,
  ESTIMATED_SCORE_MIN,
  estimatedScoreLineParts,
  formatEstimatedScoreValue,
  latestScoredDeneme,
  primaryEstimatedScore,
} from "../../../utilization/MockExamEstimatedScoreUtilization";
import i18n from "../../../language/i18n";
import { NotificationManager } from "../../../notifications/NotificationManager";
import { resolveBackendMessageKey } from "../../../utilization/BackendMessageUtilization";
import { Tooltip } from "react-tooltip";
import { Radar } from "react-chartjs-2";
import "../../../utilization/ChartSetup";
import OutcomeGraphStrongWeak from "../../common/outcomes/OutcomeGraphStrongWeak";
import OutcomeGraphGeneralStatus from "../../common/outcomes/OutcomeGraphGeneralStatus";
import SubjectDescriptionNavigationModal, {
  ISubjectDescriptionNavigationTarget,
  useSubjectDescriptionLookup,
} from "../../common/outcomes/SubjectDescriptionNavigationModal";
import SkillGraph from "../../common/skills/SkillGraph";
import {
  PerfOverview,
  ScoreLanes,
  C_STUDENT,
  C_BRANCH,
  C_CLASS,
  C_PERSONALIZED,
  C_MOCK_EXAM,
  C_HANDWRITTEN,
  C_SUPPORT,
} from "./PerfCharts";
import { perfChartsStyles } from "./PerfChartsStyles";
import {
  BaseLoading,
  IconButton,
  DividerLine,
  BaseModal as _BaseModal,
  InputWithSpecialExpression as _InputWithSpecialExpression,
  constructCombinedBranchAssignmentSkillReportMap,
  EmptyState,
  GlassSelect,
  PageTravelGroup as _PageTravelGroup,
  SecondaryButton,
} from "../../../library";
import {
  generateBranchAssignmentFeedback,
  generatePersonalizedAssignmentFeedback,
} from "../../../requests/CatchtivityRequests";
import { reportCardViewStyles } from "./ReportCardViewStyles";
import {
  personalizedAssignmentLabelOf,
  personalizedAssignmentTooltipOf,
} from "./personalizedAssignmentLabel";
import LearnerTrendChart from "./LearnerTrendChart";
import PrerequisiteWorkSection from "./PrerequisiteWorkSection";
import { buildPrerequisiteWorkGroups } from "../../../utilization/PrerequisiteWorkUtilization";
import {
  buildLearnerSourceChipList,
  orderLearnerSourceIds,
  resolveVisibleSourceIds,
  toggleLearnerSourceId,
} from "../../../utilization/LearnerSourceDefs";
import { retrieveReportViewSourceIds } from "../../../utilization/ReportTabGroupUtilization";
import {
  BRANCH_ASSIGNMENT,
  MOCK_EXAM,
  PERSONALIZED_ASSIGNMENT,
  SUPPORT_ASSIGNMENT,
  buildAssignmentReportRowList,
  buildExamReportRowList,
  reportRowScoreStateOf,
  summarizeReportRowList,
  type HolisticReportRow,
} from "../../../utilization/HolisticReportUtilization";
import AssignmentKindChip from "../assignments/AssignmentKindChip";
import CheckBoxGlyph from "../../common/CheckBoxGlyph";
import { isWithinTimeFilterWindow } from "../../../library/utilization/DateUtilization";

// TODO(ts-tighten): library types are over-strict; cast lets call sites match real runtime contract.
const BaseModal: any = _BaseModal;
const InputWithSpecialExpression: any = _InputWithSpecialExpression;
const PageTravelGroup: any = _PageTravelGroup;

const ReportCardView = (props) => {
  const {
    isMinimized,
    selectedCoterieType: selectedCoterieTypeProp,
    selectedPerformanceType,
    // Time-range filter owned by the /reports filter bar (next to "Ders").
    timeFilter = "all",
  } = props;
  const navigate = useNavigate();
  const {
    language,
    userInformation,
    userProfile,
    userProfileGrade,
    userProfileSeason,
    branchAssignmentPerformanceLoading,
    branchAssignmentPerformance,
    setBranchAssignmentPerformance,
    learnerHandwrittenExamPerformanceLoading,
    learnerMockExamPerformanceLoading,
    learnerHandwrittenExamPerformance,
    learnerMockExamPerformance,
    learnerSupportAssignmentPerformanceLoading,
    learnerSupportAssignmentPerformance,
    personalizedAssignmentPerformanceLoading,
    personalizedAssignmentPerformance,
    setPersonalizedAssignmentPerformance,
    branchAssignmentReportDetailListLoading,
    branchAssignmentReportDetailDTOList,
    learnerCompletionOutcomeReportMap,
    learnerHandwrittenOutcomeReportMap,
    learnerMockExamOutcomeReportMap,
    learnerSupportAssignmentOutcomeReportMap,
    individualModelListLoading,
    individualModelDTOList,
    categoryDTOList,
    reportCategoryDTOList,
  } = useAuth();
  // Every category this screen can name, by id. The two lists come from the same JSON under
  // different filters, so they never disagree — the union is simply the widest set available.
  //
  // The REPORT list is the one that matters: it is deliberately NOT level-filtered, and an ön koşul
  // outcome almost always belongs to a grade below the learner's own, so the level-filtered
  // `categoryDTOList` alone cannot name one. It is folded in second so it wins, and the
  // level-filtered list is kept underneath purely so naming can never come out WORSE than before
  // this change — the two are populated by different effects, and a report list that has not
  // arrived yet must not blank names the other list could already supply.
  const categoryNameByIdForNaming = useMemo(() => {
    const nameById = new Map<number, string>();
    [categoryDTOList, reportCategoryDTOList].forEach((list: any) => {
      (list || []).forEach((category: any) => {
        if (category?.id != null && category?.name) {
          nameById.set(Number(category.id), category.name);
        }
      });
    });
    return nameById;
  }, [categoryDTOList, reportCategoryDTOList]);
  // Naming context for the personalized-assignment rows: those items carry ids, not names (see
  // ./personalizedAssignmentLabel). Both lookups are already loaded for this screen — the category
  // names come from the context lists above, and the branch-assignment names from the sibling tab.
  const personalizedAssignmentNaming = useMemo(() => {
    const branchAssignmentNameById = new Map<number, string>();
    (branchAssignmentPerformance || []).forEach((item: any) => {
      if (item?.branchAssignmentId != null && item?.name) {
        branchAssignmentNameById.set(Number(item.branchAssignmentId), item.name);
      }
    });
    return {
      categoryNameById: categoryNameByIdForNaming,
      branchAssignmentNameById,
    };
  }, [categoryNameByIdForNaming, branchAssignmentPerformance]);

  const [minimizedCoterieType, setMinimizedCoterieType] =
    useState("DEFAULT_OPTION");
  const selectedCoterieType = isMinimized
    ? minimizedCoterieType
    : selectedCoterieTypeProp || "DEFAULT_OPTION";

  // Scores arrive as 0–1 fractions; the trend axis is a percentage. Deliberately NOT safeScore:
  // that coerces null → 0, which would draw a dip the learner never had. A missing score is a gap.
  const toTrendPercentage = (value: any) =>
    value === null || value === undefined || isNaN(value)
      ? null
      : Number(value) * 100;

  // Gelişim grafiği: one line per assessment source, each plotting THIS learner's score. Only şube
  // ödevi and yazılı sınav rows carry şube/sınıf comparisons — the personalized item builder never
  // sets them — so bireysel ödev draws the learner's line alone rather than an empty benchmark.
  const learnerTrendSources = useMemo(() => {
    const withinWindow = (timestamp: any) =>
      isWithinTimeFilterWindow(timestamp, timeFilter);
    const inCoterie = (item: any) =>
      selectedCoterieType === "DEFAULT_OPTION" ||
      item?.coterieType === selectedCoterieType;
    return [
      {
        id: "BRANCH_ASSIGNMENT",
        label: i18n.t("trend_source_corporate"),
        // Every source plots the learner's own score, so the line is named for WHOSE score it is
        // rather than for the source — otherwise "Şube ödevleri" would read as a şube figure while
        // carrying the learner's, right beside a "Şube puanı" that really is one.
        primaryLabel: i18n.t("learner_score"),
        color: C_STUDENT,
        benchmarkDefs: [
          { key: "branch", label: i18n.t("branch_score"), color: C_BRANCH },
          { key: "grade", label: i18n.t("grade_score"), color: C_CLASS },
        ],
        points: (branchAssignmentPerformance || [])
          .filter((item: any) => item?.isEvaluated && inCoterie(item))
          .filter((item: any) => withinWindow(item?.endAt))
          .map((item: any) => ({
            label: item.name,
            timestamp: item.endAt,
            primary: toTrendPercentage(item.learnerScore),
            benchmarks: {
              branch: toTrendPercentage(item.branchScore),
              grade: toTrendPercentage(item.gradeScore),
            },
          })),
      },
      {
        id: "SUPPORT_ASSIGNMENT",
        label: i18n.t("trend_source_support"),
        primaryLabel: i18n.t("learner_score"),
        color: C_SUPPORT,
        // No benchmarkDefs: a destek roster is hand-picked, so there is no şube / sınıf figure to
        // compare against (the contract sends null). A destek whose results are not published yet —
        // or that nothing has been graded in — has no point at all rather than a 0.
        points: (learnerSupportAssignmentPerformance || [])
          .filter(
            (item: any) =>
              item?.resultsPublished !== false &&
              item?.learnerScore != null &&
              inCoterie(item),
          )
          .filter((item: any) => withinWindow(item?.endAt))
          .map((item: any) => ({
            label: item.name,
            timestamp: item.endAt,
            primary: toTrendPercentage(item.learnerScore),
          })),
      },
      {
        id: "PERSONALIZED_ASSIGNMENT",
        label: i18n.t("trend_source_personalized"),
        primaryLabel: i18n.t("learner_score"),
        color: C_PERSONALIZED,
        points: (personalizedAssignmentPerformance || [])
          .filter((item: any) => item?.isEvaluated && inCoterie(item))
          .filter((item: any) => withinWindow(item?.endAt || item?.createdAt))
          .map((item: any) => ({
            // Never item.name — that is the deprecated "Personalized Assignment #<id>" literal.
            label: personalizedAssignmentLabelOf(item, personalizedAssignmentNaming),
            timestamp: item.endAt || item.createdAt,
            primary: toTrendPercentage(item.learnerScore),
          })),
      },
      {
        id: "HANDWRITTEN_EXAM",
        label: i18n.t("trend_source_handwritten"),
        primaryLabel: i18n.t("learner_score"),
        color: C_HANDWRITTEN,
        benchmarkDefs: [
          { key: "branch", label: i18n.t("branch_score"), color: C_BRANCH },
          { key: "grade", label: i18n.t("grade_score"), color: C_CLASS },
        ],
        // No isEvaluated filter here, unlike the two sources above — deliberate, not an omission.
        // A handwritten exam item carries no such flag (it exists only on the assignment DTOs), and
        // an ungraded paper arrives with a null learnerScore, which countPlottable already drops.
        // This matches how the staff console filters the same list.
        points: (learnerHandwrittenExamPerformance || [])
          .filter((item: any) => inCoterie(item))
          .filter((item: any) => withinWindow(item?.createdAt))
          .map((item: any) => ({
            label: item.name,
            timestamp: item.createdAt,
            primary: toTrendPercentage(item.learnerScore),
            benchmarks: {
              branch: toTrendPercentage(item.branchScore),
              grade: toTrendPercentage(item.gradeScore),
            },
          })),
      },
      {
        id: "MOCK_EXAM",
        label: i18n.t("trend_source_mock_exam"),
        primaryLabel: i18n.t("learner_score"),
        color: C_MOCK_EXAM,
        // No benchmarkDefs: the per-learner mock exam item carries no şube/sınıf comparison, so
        // this line stands alone rather than drawing two empty ones (same as bireysel ödev above).
        points: (learnerMockExamPerformance || [])
          .filter((item: any) => inCoterie(item))
          // createdAt here is already the SITTING date — normalised at ingest in LearnerApp, so this
          // source reads exactly like its siblings instead of carrying its own date rule.
          .filter((item: any) => withinWindow(item?.createdAt))
          .map((item: any) => ({
            label: item.name,
            timestamp: item.createdAt,
            primary: toTrendPercentage(item.learnerScore),
          })),
      },
    ];
  }, [
    branchAssignmentPerformance,
    personalizedAssignmentPerformance,
    learnerHandwrittenExamPerformance,
    learnerMockExamPerformance,
    learnerSupportAssignmentPerformance,
    personalizedAssignmentNaming,
    selectedCoterieType,
    timeFilter,
  ]);

  // Hangi ölçme kaynağı görünür. Bu satır üç ayrı kontrolün yerine geçiyor: gelişim grafiğinin kendi
  // tek-seçimli şeridi ve iki grafiğin kendi çip kümeleri, hepsi birbirinden bağımsızdı — biri
  // değiştirilince raporun yarısı diğerini yansıtmıyordu. `null` = okuyucu henüz seçmedi.
  const [selectedSourceIds, setSelectedSourceIds] = useState<string[] | null>(
    null,
  );
  // "Available" has to mean "will actually plot something", because `resolveDefaultVisibleSourceIds`
  // keys its fallback on this list: a source counted available but unable to draw a single row makes
  // the report open EMPTY, which is the exact failure that fallback exists to prevent.
  //
  // Each source is asked its own downstream question rather than a blanket one — the predicates
  // genuinely differ.
  //
  // For the two ödev sources the test is TRUTHY `isEvaluated`, matching the strictest consumer (the
  // gelişim chart's own filters) and the screen's own tab gate in ReportListScreen. It must not be
  // `!== false`: an evaluation — and therefore the flag — exists only once the learner has completed
  // the assignment, so an assigned-but-unsolved ödev arrives with `isEvaluated` ABSENT, not false.
  // `undefined !== false` is true, which would let exactly the row this guard exists to exclude keep
  // its source available. (`ReportDetailBuilder`'s `=== false` is the lone outlier in the codebase,
  // and it is the loosest of the three consumers, so agreeing with it is not enough here.)
  //
  // A yazılı sınav row carries no such flag at all (see the note on its trend source): an ungraded
  // paper arrives with a null learnerScore and is dropped for that instead, so asking it about
  // `isEvaluated` would call a real source unavailable — the same mismatch in the other direction.
  const availableSourceIds = useMemo(() => {
    const inCoterie = (item: any) =>
      selectedCoterieType === "DEFAULT_OPTION" ||
      item?.coterieType === selectedCoterieType;
    const has = (list: any) => (list || []).some(inCoterie);
    const hasEvaluated = (list: any) =>
      (list || []).some(
        (item: any) => item?.isEvaluated === true && inCoterie(item),
      );
    return orderLearnerSourceIds([
      ...(hasEvaluated(branchAssignmentPerformance)
        ? ["BRANCH_ASSIGNMENT"]
        : []),
      // Destek counts as soon as the learner HAS one: a destek whose results are not published yet
      // is still listed under Ödevler (saying so), and it contributes nothing elsewhere until it has
      // a score — so it can never make a tab open on a fabricated number.
      ...(has(learnerSupportAssignmentPerformance) ? ["SUPPORT_ASSIGNMENT"] : []),
      ...(hasEvaluated(personalizedAssignmentPerformance)
        ? ["PERSONALIZED_ASSIGNMENT"]
        : []),
      ...(has(learnerHandwrittenExamPerformance) ? ["HANDWRITTEN_EXAM"] : []),
      // A deneme spans every ders it tested, and its list ignores the Ders picker — so its chip does
      // too, or Sınavlar would list denemes under a disabled "no data" chip.
      ...((learnerMockExamPerformance || []).length > 0 ? ["MOCK_EXAM"] : []),
    ]);
  }, [
    branchAssignmentPerformance,
    personalizedAssignmentPerformance,
    learnerHandwrittenExamPerformance,
    learnerMockExamPerformance,
    learnerSupportAssignmentPerformance,
    selectedCoterieType,
  ]);
  // The selection is ONE axis shared by every view; each view reads it through its own SCOPE (the
  // kinds it actually consumes — Genel durum all five, Güçlü/zayıf and Ödevler the three ödev kinds,
  // Sınavlar the two exams). Resolving it within the scope is what keeps a view from being emptied
  // by its own chips while a source it never reads is still ticked elsewhere.
  const viewSourceScope = retrieveReportViewSourceIds(selectedPerformanceType);
  const visibleSourceIds = resolveVisibleSourceIds(
    selectedSourceIds,
    availableSourceIds,
    viewSourceScope,
  );
  const isSourceVisible = (sourceId: string) =>
    visibleSourceIds.includes(sourceId);
  const visibleLearnerTrendSources = learnerTrendSources.filter((source: any) =>
    isSourceVisible(source.id),
  );

  /**
   * The Kaynak row. It sits at the top of the card, directly under the screen's own filter bar —
   * which already carries Ders and Zaman aralığı — so the order a reader meets is ders, zaman, then
   * kaynak, and the row the reader already knows keeps its place.
   *
   * <p>A real <button> per source rather than this file's older click-handler divs: Space/Enter,
   * focus and the disabled state then come from the platform, and `aria-pressed` is what tells a
   * screen reader these are toggles rather than commands.
   */
  /**
   * The Kaynak chips of the CURRENT view, scoped to the kinds it reads.
   *
   * <p>Scoped, not filtered out of a global row: the old row computed the "last one standing" lock
   * against the WHOLE selection, so on Güçlü ve zayıf yönler — which reads only ödev kinds — a still
   * ticked exam kept the last ödev chip unlockable and the tab could be emptied. Within a scope the
   * lock is the view's own, and a toggle here leaves the reader's choice for other views alone.
   */
  const renderSourceFilterRow = () => {
    const chipList = buildLearnerSourceChipList(
      selectedSourceIds,
      availableSourceIds,
      viewSourceScope,
    );
    return chipList.length > 1 ? (
      <div
        className="qcw-source-row"
        role="group"
        aria-label={i18n.t("source_filter_label")}
      >
        <span className="qcw-source-row-label">
          {i18n.t("source_filter_label")}
        </span>
        {chipList.map((chip) => (
          <button
            key={chip.id}
            type="button"
            className={`qcw-source-chip${chip.checked ? " qcw-source-chip--on" : ""}${
              chip.unavailable ? " qcw-source-chip--empty" : ""
            }`}
            // Veri yoksa kontrol değil bildirim; son kalan seçili kaynak da kilitli, çünkü hepsi
            // kapatılınca rapor genel "veri bulunamadı" durumuna düşer ve geri dönüş yolu kalmaz.
            disabled={chip.unavailable || chip.lockedOn}
            aria-pressed={chip.checked}
            title={
              chip.unavailable
                ? i18n.t("source_unavailable_hint")
                : chip.lockedOn
                  ? i18n.t("source_locked_on_hint")
                  : undefined
            }
            onClick={() =>
              setSelectedSourceIds(
                toggleLearnerSourceId(
                  selectedSourceIds,
                  chip.id,
                  availableSourceIds,
                  viewSourceScope,
                ),
              )
            }
          >
            <CheckBoxGlyph checked={chip.checked} />
            <span>{i18n.t(chip.labelKey)}</span>
            {chip.unavailable ? (
              <span className="qcw-source-chip-count">0</span>
            ) : null}
          </button>
        ))}
      </div>
    ) : null;
  };

  // Ön koşul çalışmaları: the readiness practice this learner FINISHED, per ön koşul konu. Built
  // from the unfiltered list — the model applies the ders and window filters itself.
  // Both category lists, report list last so it wins — it is the one that can name (and grade) an
  // ön koşul from a lower grade. Same union as categoryNameByIdForNaming above, but the model needs
  // the DTOs themselves rather than just the names, since it also reads each konu's `level`.
  const prerequisiteWorkGroups = useMemo(
    () =>
      buildPrerequisiteWorkGroups(
        personalizedAssignmentPerformance || [],
        [...(categoryDTOList || []), ...(reportCategoryDTOList || [])],
        { coterieType: selectedCoterieType, timeFilterWithN: timeFilter },
      ),
    [
      personalizedAssignmentPerformance,
      categoryDTOList,
      reportCategoryDTOList,
      selectedCoterieType,
      timeFilter,
    ],
  );
  const [showIndividualModel, setShowIndividualModel] = useState(false);
  // Keyed by the ROW key (kind + id), not the bare id: Ödevler merges kinds, and a şube ödevi and a
  // bireysel ödev can share a numeric id — toggling one would have opened the other's feedback.
  const [expandedFeedbackKey, setExpandedFeedbackKey] = useState<string | null>(
    null,
  );
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [assignmentsPageNumber, setAssignmentsPageNumber] = useState(0);
  const [examsPageNumber, setExamsPageNumber] = useState(0);
  const ITEMS_PER_PAGE = 10;

  // "Konu Anlatımına Git" from the Genel durum outcome rows — the same jump the güçlü/zayıf cards
  // already offer, on the rows scoring under the barem. Owned HERE, not inside the graph: the graph
  // renders once per ders, so one modal per lesson card would be N dialogs over one decision, and the
  // güçlü/zayıf tab keeps its own instance because that graph resolves its own cards.
  const { findSubjectDescriptionByOutcomeCode } = useSubjectDescriptionLookup();
  const [subjectDescriptionTarget, setSubjectDescriptionTarget] =
    useState<ISubjectDescriptionNavigationTarget | null>(null);
  // The coterie the modal navigates INTO is the ders the report is showing — the Genel durum tree is
  // keyed by it, and /learning-path opens on it.
  const subjectDescriptionNavigation = {
    canNavigate: (outcomeCode?: string | null) =>
      findSubjectDescriptionByOutcomeCode(outcomeCode) !== null,
    onNavigate: setSubjectDescriptionTarget,
  };

  // A narrower ders or Kaynak selection can leave fewer pages than the one on screen.
  useEffect(() => {
    setAssignmentsPageNumber(0);
    setExamsPageNumber(0);
  }, [selectedCoterieType, selectedSourceIds]);

  // Dashboard (minimized) report card: auto-select the first available coterie so the
  // preview shows data immediately instead of the "pick a coterie" hint. The full
  // /reports screen handles its own auto-select (ReportListScreen), so this is
  // minimized-only.
  useEffect(() => {
    if (!isMinimized) return;
    if (minimizedCoterieType !== "DEFAULT_OPTION") return;
    const firstCoterie = (branchAssignmentPerformance || [])
      .filter((item) => item.isEvaluated)
      .find((item) => item.coterieType)?.coterieType;
    if (firstCoterie) setMinimizedCoterieType(firstCoterie);
  }, [branchAssignmentPerformance, isMinimized, minimizedCoterieType]);

  const safeScore = (value) =>
    value === null || value === undefined || isNaN(value) ? 0 : value;

  // Memoized id→reportDetail lookup: the branch-assignment row renderer
  // previously did a `.find()` over the full report-detail list once PER ROW
  // (10 rows/page → 10 linear scans every render). Build the index once and do
  // O(1) lookups instead. Rebuilt only when the source list changes.
  const corporateReportDetailById = useMemo(() => {
    const map = new Map();
    for (const rd of branchAssignmentReportDetailDTOList || []) {
      if (rd && rd.id != null) map.set(rd.id, rd);
    }
    return map;
  }, [branchAssignmentReportDetailDTOList]);

  const filterCoterieTypeOptionList = () => {
    const coterieTypeOptionList = [];
    filteredBranchAssignmentDTOList.forEach((item) => {
      if (
        !coterieTypeOptionList.find(
          (coterieTypeOption) => coterieTypeOption.value === item.coterieType,
        )
      ) {
        coterieTypeOptionList.push({
          value: item.coterieType,
          text: i18n.t(item.coterieType),
        });
      }
    });
    return coterieTypeOptionList;
  };

  const filterBranchAssignmentPerformanceDTOListByCoterieType =
    () => {
      if (selectedCoterieType === "DEFAULT_OPTION") {
        return [];
      } else {
        const filtered = filteredBranchAssignmentDTOList
          .filter((item) => item.coterieType === selectedCoterieType)
          .reverse();
        return isMinimized ? filtered.slice(0, 5) : filtered;
      }
    };

  const filterBranchAssignmentDTOList = () => {
    if (!branchAssignmentPerformance) return [];
    return branchAssignmentPerformance.filter((item) => item.isEvaluated);
  };

  const constructRadarDifficultyLabels = () => {
    return [i18n.t("EASY"), i18n.t("MEDIUM"), i18n.t("HARD")];
  };

  const constructRadarActivityTemplateLabels = () => {
    return [
      i18n.t("ORDERING"),
      i18n.t("DROPDOWN"),
      i18n.t("MCSA"),
      i18n.t("MCMA"),
      i18n.t("MATCHING"),
      i18n.t("GROUPING"),
      i18n.t("FILL_IN_THE_BLANKS"),
      i18n.t("OPEN_ENDED"),
      i18n.t("TRUE_FALSE"),
    ];
  };

  const constructRadarDifficultyData = (coterieField) => {
    const individualModel = individualModelDTOList.find(
      (item) => item.coterieField === coterieField,
    );
    if (!individualModel) return;
    return {
      labels: constructRadarDifficultyLabels(),
      datasets: [
        {
          label: i18n.t(`${coterieField.toLowerCase()}_difficulty_tendency`),
          data: [
            individualModel.easy,
            individualModel.medium,
            individualModel.hard,
          ],
          backgroundColor:
            coterieField === "NUMERIC"
              ? "rgba(17, 72, 89, 0.3)"
              : "rgba(0, 44, 58, 0.3)",
          borderColor:
            coterieField === "NUMERIC"
              ? "rgba(17, 72, 89, 1)"
              : "rgba(0, 44, 58, 1)",
          borderWidth: 1,
        },
      ],
    };
  };

  const constructRadarActivityTemplateData = (coterieField) => {
    const individualModel = individualModelDTOList.find(
      (item) => item.coterieField === coterieField,
    );
    if (!individualModel) return;
    return {
      labels: constructRadarActivityTemplateLabels(),
      datasets: [
        {
          label: i18n.t(
            `${coterieField.toLowerCase()}_activity_template_tendency`,
          ),
          data: [
            individualModel.ordering,
            individualModel.dropdown,
            individualModel.mcsa,
            individualModel.mcma,
            individualModel.matching,
            individualModel.grouping,
            individualModel.fillInTheBlanks,
            individualModel.openEnded,
            individualModel.trueFalse,
          ],
          backgroundColor:
            coterieField === "NUMERIC"
              ? "rgba(17, 72, 89, 0.3)"
              : "rgba(0, 44, 58, 0.3)",
          borderColor:
            coterieField === "NUMERIC"
              ? "rgba(17, 72, 89, 1)"
              : "rgba(0, 44, 58, 1)",
          borderWidth: 1,
        },
      ],
    };
  };

  const constructRadarOptions = (radarData) => {
    let suggestedMin = 0;
    let suggestedMax = 1;
    if (radarData) {
      const { datasets } = radarData;
      const dataset = datasets[0];
      const { data } = dataset;
      suggestedMin = Math.min(...data);
      suggestedMax = Math.max(...data);
    }

    return {
      scales: {
        r: {
          angleLines: {
            display: true,
          },
          pointLabels: {
            display: true,
          },
          ticks: {
            display: false,
          },
          suggestedMin,
          suggestedMax,
        },
      },
    };
  };

  const handleBranchAssignmentFeedbackOnClick = async (
    branchAssignmentId,
    rowKey: string,
  ) => {
    setFeedbackLoading(true);
    const { data, err } =
      await generateBranchAssignmentFeedback({
        userId: userInformation.id,
        userProfileId: userProfile.id,
        brandId: userProfileSeason.institutionDTO.campusDTO.brandDTO.id,
        campusId: userProfileSeason.institutionDTO.campusDTO.id,
        institutionId: userProfileSeason.institutionDTO.id,
        branchAssignmentId,
        firstName: userInformation.firstName,
      });

    if (err) {
      // Was a bare `return`: the spinner was never cleared, so one refusal disabled this button for the
      // rest of the session with nothing on screen. And this is the learner surface behind the relay
      // that used to turn a spent AI budget into an opaque 500, so it is exactly where "AI limitine
      // ulaştın" has to appear rather than silence.
      setFeedbackLoading(false);
      NotificationManager.error(
        i18n.t(resolveBackendMessageKey(err, "report_feedback_failed")),
      );
      return;
    }

    const foundItem = (branchAssignmentPerformance || []).find(
      (item) =>
        item.branchAssignmentId === parseFloat(branchAssignmentId),
    );

    if (foundItem) {
      const updatedList = (branchAssignmentPerformance || []).map((item) =>
        item.branchAssignmentId === parseFloat(branchAssignmentId)
          ? { ...item, aiFeedbackText: (data as any).aiFeedbackText }
          : item,
      );
      setBranchAssignmentPerformance(updatedList);
      handleFeedbackToggleOnClick(rowKey);
    }
    setFeedbackLoading(false);
  };

  const handleFeedbackToggleOnClick = (rowKey: string) => {
    setExpandedFeedbackKey((current) => (current === rowKey ? null : rowKey));
  };

  const handlePersonalizedAssignmentFeedbackOnClick = async (
    personalizedAssignmentId,
    rowKey: string,
  ) => {
    setFeedbackLoading(true);
    const { data, err } = await generatePersonalizedAssignmentFeedback({
      userId: userInformation.id,
      userProfileId: userProfile.id,
      brandId: userProfileSeason.institutionDTO.campusDTO.brandDTO.id,
      campusId: userProfileSeason.institutionDTO.campusDTO.id,
      institutionId: userProfileSeason.institutionDTO.id,
      personalizedAssignmentId,
      firstName: userInformation.firstName,
    });

    if (err) {
      setFeedbackLoading(false);
      // Cleared its spinner but said nothing, so a refusal looked like a button that does nothing.
      NotificationManager.error(
        i18n.t(resolveBackendMessageKey(err, "report_feedback_failed")),
      );
      return;
    }

    const updatedList = (personalizedAssignmentPerformance || []).map((item) =>
      item.personalizedAssignmentId === parseFloat(personalizedAssignmentId)
        ? { ...item, aiFeedbackText: (data as any).aiFeedbackText }
        : item,
    );
    setPersonalizedAssignmentPerformance(updatedList);
    handleFeedbackToggleOnClick(rowKey);
    setFeedbackLoading(false);
  };

  // Date cell for the design rp-list grid: bold date over a muted time.
  const renderRpDate = (dateValue) => {
    const d = new Date(dateValue);
    return (
      <>
        <b>
          {d.toLocaleDateString(language, {
            day: "numeric",
            month: "short",
            year: "numeric",
          })}
        </b>
        <small>
          {d.toLocaleTimeString(language, {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </small>
      </>
    );
  };

  // Status pill tone + glyph for the design rp-status (ok = green ✓, else warn).
  const rpStatus = (status) => {
    const s = (status || "").toUpperCase();
    const ok =
      s.includes("COMPLET") ||
      s.includes("EVALUAT") ||
      s.includes("FINISH") ||
      s.includes("DONE");
    return { cls: ok ? "ok" : "warn", sym: ok ? "✓" : "⚠" };
  };

  // Progress cell for the design rp-list grid (coral fill when below 50%).
  const renderRpProg = (p) => (
    <div className="rp-prog">
      <div className="rp-prog__bar">
        <span
          style={{
            width: Math.max(p, 3) + "%",
            background: p < 50 ? "var(--accent-coral)" : "var(--brand)",
          }}
        />
      </div>
      <span className={`rp-prog__val${p < 50 ? " is-danger" : ""}`}>
        {p.toFixed(1)}%
      </span>
    </div>
  );

  // Dashboard-card variant: just the corporate "score lanes" (student bar +
  // branch/class diamond markers), titleless and cardless. Reuses the same
  // last-5 list and series as the full /reports overview.
  const RenderMinimizedScoreLanes = () => {
    const corporatePerfList =
      filterBranchAssignmentPerformanceDTOListByCoterieType();
    const corporateSeries = [
      { key: "student", label: i18n.t("learner_score"), color: C_STUDENT },
      { key: "branch", label: i18n.t("branch_score"), color: C_BRANCH },
      { key: "class", label: i18n.t("grade_score"), color: C_CLASS },
    ];
    const corporateGroups = corporatePerfList.slice(0, 10).map((item) => ({
      label: item.name,
      values: {
        student: safeScore(item.learnerScore) * 100,
        branch: safeScore(item.branchScore) * 100,
        class: safeScore(item.gradeScore) * 100,
      },
    }));
    return (
      <ScoreLanes
        title=""
        groups={corporateGroups}
        series={corporateSeries}
        emptyText={i18n.t("no_chart_data_yet")}
      />
    );
  };

  const RenderMinimizedGraphicalData = () => {
    if (!branchAssignmentPerformance) return;
    return (
      <div className="qcw-minimized-root">
        <style>{reportCardViewStyles}</style>
        <style>{perfChartsStyles}</style>
        {/* Labeled "Ders" glass dropdown — matches the full /reports filter bar
            (was a SelectionTabPill tab strip). */}
        <div className="qcw-fgroup">
          <span className="qcw-fgroup-lbl">{i18n.t("lesson")}</span>
          <GlassSelect
            options={filteredCoterieTypeOptionList.map((o) => ({
              value: o.value,
              label: o.text,
            }))}
            value={selectedCoterieType}
            onChange={(id) => setMinimizedCoterieType(id)}
            ariaLabel={i18n.t("lesson")}
          />
        </div>
        {selectedCoterieType === "DEFAULT_OPTION" ? (
          <EmptyState
            compact
            icon="chartBar"
            title={i18n.t(
              "by_selecting_a_coterie_type_you_can_reach_your_reports_text",
            )}
          />
        ) : (
          // Dashboard preview shows ONLY the "Genel kurumsal ödev puanları"
          // score-lanes (the full /reports screen's donut rings + inner card +
          // chart title are dropped — the Raporlar card already provides a card).
          // The list is already the last 5 (filter…ByCoterieType + isMinimized).
          <div className="qcw-mini-perf qcw-flex-1-min-0">
            {RenderMinimizedScoreLanes()}
          </div>
        )}
      </div>
    );
  };

  const RenderIndividualModelModal = () => {
    return (
      <BaseModal
        title={i18n.t("individual_model")}
        icon="📊"
        isOpen={showIndividualModel}
        onAfterOpen={() => {}}
        onRequestClose={() => {
          setShowIndividualModel(false);
        }}
      >
        {individualModelListLoading ? (
          <BaseLoading
            size="large"
            primaryColor="#57C2D3"
            secondaryColor="#57C2D3"
          />
        ) : (
          <>
            <style>{reportCardViewStyles}</style>
            <div className="qcw-modal-section">
              <div className="qcw-modal-section-inner">
                <div className="qcw-rotated-label">
                  <span className="qcw-rotated-label-text">
                    {i18n.t("difficulty")}
                  </span>
                </div>
                <div className="qcw-radar-pair">
                  <div className="qcw-radar-half">
                    <Radar
                      data={verbalRadarDifficultyData}
                      options={verbalRadarDifficultyOptions}
                    />
                  </div>
                  <div className="qcw-radar-half">
                    <Radar
                      data={numericRadarDifficultyData}
                      options={numericRadarDifficultyOptions}
                    />
                  </div>
                </div>
              </div>
            </div>
            <DividerLine />
            <div className="qcw-modal-section">
              <div className="qcw-modal-section-inner">
                <div className="qcw-rotated-label">
                  <span className="qcw-rotated-label-text">
                    {i18n.t("template")}
                  </span>
                </div>
                <div className="qcw-radar-pair">
                  <div className="qcw-radar-half">
                    <Radar
                      data={verbalRadarActivityTemplateData}
                      options={verbalRadarActivityTemplateOptions}
                    />
                  </div>
                  <div className="qcw-radar-half">
                    <Radar
                      data={numericRadarActivityTemplateData}
                      options={numericRadarActivityTemplateOptions}
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </BaseModal>
    );
  };

  const buildOutcomeMapsForCurrentCoterie = () => {
    const filteredBranchAssignmentReportDetailList =
      selectedCoterieType === "DEFAULT_OPTION"
        ? []
        : (branchAssignmentReportDetailDTOList || []).filter(
            (reportDetail) =>
              reportDetail.branchAssignmentDTO?.coterieType ===
              selectedCoterieType,
          );
    const branchAssignmentReportMap = buildMergedBranchAssignmentReportMap(
      filteredBranchAssignmentReportDetailList,
    );
    const personalizedAssignmentReportMap =
      selectedCoterieType === "DEFAULT_OPTION"
        ? {}
        : {
            [selectedCoterieType]:
              (learnerCompletionOutcomeReportMap || {})[selectedCoterieType] ||
              [],
          };
    const handwrittenReportMap =
      selectedCoterieType === "DEFAULT_OPTION"
        ? {}
        : {
            [selectedCoterieType]:
              (learnerHandwrittenOutcomeReportMap || {})[selectedCoterieType] ||
              [],
          };
    const mockExamReportMap =
      selectedCoterieType === "DEFAULT_OPTION"
        ? {}
        : {
            [selectedCoterieType]:
              (learnerMockExamOutcomeReportMap || {})[selectedCoterieType] ||
              [],
          };
    const supportAssignmentReportMap =
      selectedCoterieType === "DEFAULT_OPTION"
        ? {}
        : {
            [selectedCoterieType]:
              (learnerSupportAssignmentOutcomeReportMap || {})[
                selectedCoterieType
              ] || [],
          };
    return {
      branchAssignmentReportMap,
      personalizedAssignmentReportMap,
      handwrittenReportMap,
      mockExamReportMap,
      supportAssignmentReportMap,
    };
  };

  const RenderStrongWeakContent = () => {
    if (
      branchAssignmentReportDetailListLoading ||
      branchAssignmentReportDetailListLoading
    ) {
      return (
        <BaseLoading
          size="large"
          primaryColor="#57C2D3"
          secondaryColor="#57C2D3"
        />
      );
    }
    const {
      branchAssignmentReportMap,
      personalizedAssignmentReportMap,
      supportAssignmentReportMap,
    } = buildOutcomeMapsForCurrentCoterie();
    // Kaynak seçimi kartların SAYISINI da belirliyor, yalnızca içeriğini değil: anahtarlar
    // filtrelenmemiş haritalardan gelirken, verisinin tamamı kapatılmış bir kaynaktan gelen ders için
    // boş bir kart açılıyordu — filtrenin dışladığını söylediği şeyin yerinde duran bir kabuk.
    const combinedKeys = new Set([
      ...(isSourceVisible("BRANCH_ASSIGNMENT")
        ? Object.keys(branchAssignmentReportMap)
        : []),
      ...(isSourceVisible("SUPPORT_ASSIGNMENT")
        ? Object.keys(supportAssignmentReportMap).filter(
            (k) => (supportAssignmentReportMap[k] || []).length > 0,
          )
        : []),
      ...(isSourceVisible("PERSONALIZED_ASSIGNMENT")
        ? Object.keys(personalizedAssignmentReportMap).filter(
            (k) => (personalizedAssignmentReportMap[k] || []).length > 0,
          )
        : []),
    ]);
    return (
      <div className="qcw-content-col">
        <style>{reportCardViewStyles}</style>
        {selectedCoterieType === "DEFAULT_OPTION" ? (
          <EmptyState
            compact
            icon="chartBar"
            title={i18n.t(
              "by_selecting_a_coterie_type_you_can_reach_your_reports_text",
            )}
          />
        ) : combinedKeys.size === 0 ? (
          <>
            {renderSourceFilterRow()}
            <EmptyState compact icon="chartBar" title={i18n.t("no_outcome_data")} />
          </>
        ) : (
          <>
            {renderSourceFilterRow()}
            {[...combinedKeys].map((key) => (
              <div className="qcw-content-col" key={key}>
                <OutcomeGraphStrongWeak
                  branchAssignmentOutcomeReportList={
                    branchAssignmentReportMap[key] || []
                  }
                  personalizedAssignmentOutcomeReportList={
                    personalizedAssignmentReportMap[key] || []
                  }
                  supportAssignmentOutcomeReportList={
                    supportAssignmentReportMap[key] || []
                  }
                  coterieType={key}
                  timeFilter={timeFilter}
                  visibleSourceIds={visibleSourceIds}
                />
                <DividerLine />
              </div>
            ))}
          </>
        )}
      </div>
    );
  };

  const RenderGeneralStatusContent = () => {
    if (
      branchAssignmentReportDetailListLoading ||
      branchAssignmentReportDetailListLoading
    ) {
      return (
        <BaseLoading
          size="large"
          primaryColor="#57C2D3"
          secondaryColor="#57C2D3"
        />
      );
    }
    const {
      branchAssignmentReportMap,
      personalizedAssignmentReportMap,
      handwrittenReportMap,
      mockExamReportMap,
      supportAssignmentReportMap,
    } = buildOutcomeMapsForCurrentCoterie();
    // Aynı gerekçe: kapatılmış bir kaynağın tek başına doğurduğu ders kartı hiç açılmasın.
    const combinedKeys = new Set([
      ...(isSourceVisible("BRANCH_ASSIGNMENT")
        ? Object.keys(branchAssignmentReportMap)
        : []),
      ...(isSourceVisible("PERSONALIZED_ASSIGNMENT")
        ? Object.keys(personalizedAssignmentReportMap).filter(
            (k) => (personalizedAssignmentReportMap[k] || []).length > 0,
          )
        : []),
      ...(isSourceVisible("HANDWRITTEN_EXAM")
        ? Object.keys(handwrittenReportMap).filter(
            (k) => (handwrittenReportMap[k] || []).length > 0,
          )
        : []),
      ...(isSourceVisible("MOCK_EXAM")
        ? Object.keys(mockExamReportMap).filter(
            (k) => (mockExamReportMap[k] || []).length > 0,
          )
        : []),
      ...(isSourceVisible("SUPPORT_ASSIGNMENT")
        ? Object.keys(supportAssignmentReportMap).filter(
            (k) => (supportAssignmentReportMap[k] || []).length > 0,
          )
        : []),
    ]);
    return (
      // qcw-stack: the trend chart, the ön koşul card and each ders tree carry no outer margin of
      // their own, so without a gap here they stack flush against one another.
      <div className="qcw-content-col qcw-stack">
        <style>{reportCardViewStyles}</style>
        {selectedCoterieType === "DEFAULT_OPTION" ? (
          <EmptyState
            compact
            icon="chartBar"
            title={i18n.t(
              "by_selecting_a_coterie_type_you_can_reach_your_reports_text",
            )}
          />
        ) : (
          <>
            {/* The chart and the ön koşul card render on their OWN evidence: a learner can have a
                score history with no outcome tree yet, or readiness work on konu no outcome graph
                covers. Leaving them inside the tree's empty state would hide both. */}
            {renderSourceFilterRow()}
            <LearnerTrendChart
              title={i18n.t("learner_improvement_over_time")}
              subtitle={i18n.t("improvement_chart_hint")}
              sources={visibleLearnerTrendSources}
              emptyText={i18n.t("not_enough_data_for_trend")}
              language={language}
              // Kaynağı artık yukarıdaki satır seçiyor; grafiğin kendi tek-seçimli şeridi kapalı.
              // Şerit kapansa da kaynak legend DIŞINDA (o satırda) adlandırılıyor — bu yüzden model
              // bayrağı ayrıca veriliyor, yoksa legend sessizce kapsam yerine kaynağı adlandırmaya
              // dönerdi.
              showSourceTabs={false}
              sourceNamedElsewhere
            />
            {/* Kaynak filtresinin KAPSAMI içinde: ön koşul çalışmalarının tamamı BİREYSEL ödevdir,
                ama bu bölüm çiplerin dışında kaldığı için "Bireysel ödev" kapatıldığında bile
                ekranda kalıyordu — filtrenin dışladığı kaynağın paneli görünür durumda kalıyor. */}
            {isSourceVisible("PERSONALIZED_ASSIGNMENT") ? (
              <PrerequisiteWorkSection
                groups={prerequisiteWorkGroups}
                language={language}
              />
            ) : null}
            {combinedKeys.size === 0 ? (
              <EmptyState
                compact
                icon="chartBar"
                title={i18n.t("no_outcome_data")}
              />
            ) : null}
            {[...combinedKeys].map((key) => (
              <div className="qcw-content-col" key={key}>
                <OutcomeGraphGeneralStatus
                  branchAssignmentOutcomeReportList={
                    branchAssignmentReportMap[key] || []
                  }
                  personalizedAssignmentOutcomeReportList={
                    personalizedAssignmentReportMap[key] || []
                  }
                  handwrittenOutcomeReportList={
                    handwrittenReportMap[key] || []
                  }
                  mockExamOutcomeReportList={
                    mockExamReportMap[key] || []
                  }
                  supportAssignmentOutcomeReportList={
                    supportAssignmentReportMap[key] || []
                  }
                  coterieType={key}
                  timeFilter={timeFilter}
                  subjectDescriptionNavigation={subjectDescriptionNavigation}
                  // Kontrol yukarıda, tek bir satırda; filtreleme hâlâ bileşenin içinde. Çipler
                  // grafiğin içindeyken her DERS için ayrı bir satır doğuyordu.
                  visibleSourceIds={visibleSourceIds}
                />
                <DividerLine />
              </div>
            ))}
          </>
        )}
      </div>
    );
  };

  const RenderSkillsContent = () => {
    if (
      branchAssignmentReportDetailListLoading ||
      branchAssignmentReportDetailListLoading
    ) {
      return (
        <BaseLoading
          size="large"
          primaryColor="#57C2D3"
          secondaryColor="#57C2D3"
        />
      );
    }
    const filteredBranchAssignmentReportDetailList =
      selectedCoterieType === "DEFAULT_OPTION"
        ? []
        : (branchAssignmentReportDetailDTOList || []).filter(
            (reportDetail) =>
              reportDetail.branchAssignmentDTO?.coterieType ===
              selectedCoterieType,
          );
    const branchAssignmentReportMap = buildMergedBranchAssignmentReportMap(
      filteredBranchAssignmentReportDetailList,
    );
    const branchAssignmentSkillReportMap =
      constructCombinedBranchAssignmentSkillReportMap(
        filteredBranchAssignmentReportDetailList,
      );
    const personalizedAssignmentReportMap =
      selectedCoterieType === "DEFAULT_OPTION"
        ? {}
        : {
            [selectedCoterieType]:
              (learnerCompletionOutcomeReportMap || {})[selectedCoterieType] ||
              [],
          };
    // The Kaynak row governs THIS tab too. Its key set decides how many ders cards open, so an
    // unticked source must not conjure a card whose contents the graph then filters away to nothing.
    const allKeys = new Set([
      ...(isSourceVisible("BRANCH_ASSIGNMENT")
        ? Object.keys(branchAssignmentReportMap)
        : []),
      ...(isSourceVisible("BRANCH_ASSIGNMENT")
        ? Object.keys(branchAssignmentSkillReportMap)
        : []),
      ...(isSourceVisible("PERSONALIZED_ASSIGNMENT")
        ? Object.keys(personalizedAssignmentReportMap).filter(
            (k) => (personalizedAssignmentReportMap[k] || []).length > 0,
          )
        : []),
    ]);
    return (
      <div className="qcw-content-col">
        <style>{reportCardViewStyles}</style>
        {selectedCoterieType === "DEFAULT_OPTION" ? (
          <EmptyState
            compact
            icon="chartBar"
            title={i18n.t(
              "by_selecting_a_coterie_type_you_can_reach_your_reports_text",
            )}
          />
        ) : allKeys.size === 0 ? (
          <EmptyState compact icon="chartBar" title={i18n.t("no_skill_data")} />
        ) : (
          <>
            {[...allKeys].map((key) => (
              <div className="qcw-content-col" key={key}>
                <SkillGraph
                  branchAssignmentOutcomeReportList={
                    branchAssignmentReportMap[key] || []
                  }
                  personalizedAssignmentOutcomeReportList={
                    personalizedAssignmentReportMap[key] || []
                  }
                  branchAssignmentSkillReportList={
                    branchAssignmentSkillReportMap[key] || []
                  }
                  coterieType={key}
                  visibleSourceIds={visibleSourceIds}
                />
                <DividerLine />
              </div>
            ))}
          </>
        )}
      </div>
    );
  };

  const filteredBranchAssignmentDTOList =
    filterBranchAssignmentDTOList();
  const filteredCoterieTypeOptionList = filterCoterieTypeOptionList();
  const verbalRadarDifficultyData = constructRadarDifficultyData("VERBAL");
  const verbalRadarDifficultyOptions = constructRadarOptions(
    verbalRadarDifficultyData,
  );
  const numericRadarDifficultyData = constructRadarDifficultyData("NUMERIC");
  const numericRadarDifficultyOptions = constructRadarOptions(
    numericRadarDifficultyData,
  );
  const verbalRadarActivityTemplateData =
    constructRadarActivityTemplateData("VERBAL");
  const verbalRadarActivityTemplateOptions = constructRadarOptions(
    verbalRadarActivityTemplateData,
  );
  const numericRadarActivityTemplateData =
    constructRadarActivityTemplateData("NUMERIC");
  const numericRadarActivityTemplateOptions = constructRadarOptions(
    numericRadarActivityTemplateData,
  );

  /** Why a Tahmini puan is an estimate, and what a partial one is missing — the chip's tooltip. */
  const estimatedScoreTitleOf = (part: any) => {
    const notes = [i18n.t("estimated_score_note")];
    if (part.missingTyt) notes.push(i18n.t("estimated_score_missing_tyt"));
    if (part.missingTestKeyList.length > 0) {
      notes.push(i18n.t("estimated_score_missing_tests", {
        tests: part.missingTestKeyList.map((key: string) => i18n.t(key)).join(", "),
      }));
    }
    return notes.join(" ");
  };

  /**
   * A deneme's Tahmini puan chips (an AYT carries SAY/EA/SÖZ/DİL, partial ones marked "kısmi"). Its OWN
   * column — a 100–500 score is a different quantity from Başarı %, so it is never folded into the %.
   * A deneme without a score (Diğer / no biçim) says no score is calculated.
   */
  const renderEstimatedScoreCell = (item: any) => {
    const parts = estimatedScoreLineParts(item);
    if (parts.length === 0) {
      return <small className="rp-score-none">{i18n.t("mock_exam_no_score")}</small>;
    }
    return (
      <span className="rp-score-chips">
        {parts.map((part) => (
          <span
            key={part.scoreType}
            className={`rp-score-chip${part.partial ? " rp-score-chip--partial" : ""}`}
            title={estimatedScoreTitleOf(part)}
          >
            <span className="rp-score-chip__type">{i18n.t(`score_type_${part.scoreType}`)}</span>
            <b className="rp-score-chip__value">{part.valueText}</b>
            {part.partial ? (
              <span className="rp-score-chip__partial">{i18n.t("estimated_score_partial")}</span>
            ) : null}
          </span>
        ))}
      </span>
    );
  };

  // A bireysel ödev carries ids, not a name — it is named by the shared label helper everywhere (row,
  // lane and tooltip), never by its deprecated "Personalized Assignment #<id>" literal.
  const rowLabelOf = (row: HolisticReportRow) =>
    row.kind === PERSONALIZED_ASSIGNMENT
      ? personalizedAssignmentLabelOf(row.item, personalizedAssignmentNaming)
      : row.item?.name;
  const rowTooltipOf = (row: HolisticReportRow) =>
    row.kind === PERSONALIZED_ASSIGNMENT
      ? personalizedAssignmentTooltipOf(row.item, personalizedAssignmentNaming)
      : row.item?.description || "";

  /** Each kind opens its OWN detail; a kind without a learner detail page shows a dash. */
  const rowDetailPathOf = (row: HolisticReportRow): string | null => {
    switch (row.kind) {
      case BRANCH_ASSIGNMENT: {
        const reportDetail = corporateReportDetailById.get(row.id);
        return reportDetail ? `/reports/branch-assignments/${reportDetail.id}` : null;
      }
      case SUPPORT_ASSIGNMENT:
        return row.id != null ? `/support-assignments/${row.id}` : null;
      case PERSONALIZED_ASSIGNMENT:
        return row.id != null ? `/personalized-assignments/${row.id}` : null;
      case MOCK_EXAM:
        return row.id != null ? `/reports/mock-exams/${row.id}` : null;
      default:
        return null;
    }
  };

  const renderRowName = (row: HolisticReportRow, index: number) => {
    const tooltipId = `perf-row-desc-${row.key}`;
    return (
      <span className="rp-lc rp-lc--name">
        <span className="rp-idx">{index + 1}</span>
        <Tooltip id={tooltipId} />
        <IconButton
          className="rp-iico"
          name="info"
          size="small"
          data-tooltip-id={tooltipId}
          data-tooltip-content={rowTooltipOf(row)}
          data-tooltip-place="top"
          data-tooltip-variant="info"
        />
        <span className="rp-name-stack">
          <AssignmentKindChip type={row.kind} />
          <span className="rp-name-text">{rowLabelOf(row)}</span>
        </span>
      </span>
    );
  };

  const renderRowStatus = (row: HolisticReportRow) => {
    if (!row.completionStatus) return <span className="rp-dash">—</span>;
    const st = rpStatus(row.completionStatus);
    return (
      <span className={`rp-status rp-status--${st.cls}`}>
        {st.sym} {i18n.t(row.completionStatus)}
      </span>
    );
  };

  // Same "N%" form the progress bar beside it prints.
  const percentText = (score: number) => `${Math.round(score * 100)}%`;

  /**
   * Başarı %. A destek with results the teacher has not published says so, and a destek or bireysel
   * with no score yet (a bireysel not evaluated, a destek nothing graded in) says "Değerlendirilmedi" —
   * never a 0%, which would read as a fail the learner never had. A destek with a "before" figure adds
   * before → after under its bar.
   */
  const renderRowScore = (row: HolisticReportRow) => {
    const scoreState = reportRowScoreStateOf(row);
    if (scoreState === "RESULTS_WITHHELD") {
      return (
        <span className="rp-score-state">
          {i18n.t("support_assignment_results_not_published_title")}
        </span>
      );
    }
    if (scoreState === "NOT_EVALUATED") {
      return <span className="rp-score-state">{i18n.t("NOT_EVALUATED")}</span>;
    }
    if (row.kind === SUPPORT_ASSIGNMENT && row.learnerScore != null) {
      return (
        <span className="rp-score-fallback">
          {renderRpProg(row.learnerScore * 100)}
          {row.baselineScore != null ? (
            <small
              className={`rp-score-delta${
                row.scoreDelta != null && row.scoreDelta < 0 ? " is-down" : ""
              }`}
            >
              {i18n.t("support_assignment_before_after", {
                before: percentText(row.baselineScore),
                after: percentText(row.learnerScore),
              })}
            </small>
          ) : null}
        </span>
      );
    }
    return renderRpProg(safeScore(row.learnerScore) * 100);
  };

  const renderRowDetail = (row: HolisticReportRow) => {
    const path = rowDetailPathOf(row);
    if (!path) return <span className="rp-dash">—</span>;
    const label = i18n.t(row.kind === MOCK_EXAM ? "mock_exam_open_report" : "detail");
    return (
      <button
        className="rp-eye"
        aria-label={label}
        title={label}
        onClick={() => navigate(path)}
      >
        👁
      </button>
    );
  };

  /** AI geri bildirimi — the şube and bireysel rows keep it exactly as their own tabs had it. */
  const renderRowFeedback = (row: HolisticReportRow) => {
    const { item } = row;
    if (item?.aiFeedbackText) {
      return (
        <button
          className="rp-eye"
          aria-label={i18n.t("feedback")}
          onClick={() => handleFeedbackToggleOnClick(row.key)}
        >
          {expandedFeedbackKey === row.key ? "▲" : "▼"}
        </button>
      );
    }
    const canRequest =
      row.kind === BRANCH_ASSIGNMENT
        ? item?.completionStatus !== "NOT_STARTED"
        : row.kind === PERSONALIZED_ASSIGNMENT
          ? item?.isEvaluated &&
            Array.isArray(item?.activityScoreDetailDTOList) &&
            item.activityScoreDetailDTOList.length > 0
          : false;
    if (!canRequest) return <span className="rp-dash">—</span>;
    return (
      <SecondaryButton
        {...({
          title: i18n.t("feedback"),
          size: "small",
          textOnly: true,
          disabled: feedbackLoading,
          onClick: async () => {
            if (row.kind === BRANCH_ASSIGNMENT) {
              await handleBranchAssignmentFeedbackOnClick(row.id, row.key);
            } else {
              await handlePersonalizedAssignmentFeedbackOnClick(row.id, row.key);
            }
          },
        } as any)}
      />
    );
  };

  const renderRowFeedbackPanel = (row: HolisticReportRow) =>
    row.item?.aiFeedbackText ? (
      <div className="rp-fb-wrap">
        <div
          className={`rp-fb-panel ${
            expandedFeedbackKey === row.key ? "rp-fb-panel--open" : "rp-fb-panel--closed"
          }`}
        >
          <InputWithSpecialExpression
            value={row.item.aiFeedbackText}
            showSpecialOnly={false}
            className="qcw-w-full"
          />
        </div>
      </div>
    ) : null;

  const renderPager = (
    pageNumber: number,
    setPageNumber: (page: number) => void,
    totalPages: number,
  ) =>
    totalPages > 1 ? (
      <div className="qcw-pager">
        <PageTravelGroup
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          setImageReady={() => {}}
          isImageProcessing={false}
          initialTotalPageNumber={totalPages}
        />
      </div>
    ) : null;

  /**
   * Rings + lanes over the merged rows of the selected kinds. The rings compare the learner with şube /
   * sınıf over the SAME rows — those carrying a benchmark (summarizeReportRowList); with none, only the
   * learner ring is drawn, over every scored row. A destek / bireysel row has no benchmark, so the lanes
   * draw no benchmark marker on it.
   */
  const renderRowsOverview = (rowList: HolisticReportRow[], chartTitleKey: string) => {
    const summary = summarizeReportRowList(rowList);
    const hasBranchData = summary.branchScore != null;
    const hasGradeData = summary.gradeScore != null;
    const toPct = (value: number | null) => (value == null ? null : value * 100);
    const series = [
      { key: "student", label: i18n.t("learner_score"), color: C_STUDENT },
      ...(hasBranchData
        ? [{ key: "branch", label: i18n.t("branch_score"), color: C_BRANCH }]
        : []),
      ...(hasGradeData
        ? [{ key: "class", label: i18n.t("grade_score"), color: C_CLASS }]
        : []),
    ];
    const groups = rowList.slice(0, 10).map((row) => ({
      label: rowLabelOf(row),
      values: {
        student: toPct(row.learnerScore),
        branch: hasBranchData ? toPct(row.branchScore) : null,
        class: hasGradeData ? toPct(row.gradeScore) : null,
      },
    }));
    const rings = [
      {
        key: "student",
        label: i18n.t("overall_learner_scores"),
        short: i18n.t("learner_score"),
        value: toPct(summary.learnerScore),
        color: C_STUDENT,
      },
      ...(hasBranchData
        ? [
            {
              key: "branch",
              label: i18n.t("overall_branch_scores"),
              short: i18n.t("branch_score"),
              value: toPct(summary.branchScore),
              color: C_BRANCH,
            },
          ]
        : []),
      ...(hasGradeData
        ? [
            {
              key: "class",
              label: i18n.t("overall_grade_scores"),
              short: i18n.t("grade_score"),
              value: toPct(summary.gradeScore),
              color: C_CLASS,
            },
          ]
        : []),
    ];
    const goalRing =
      !hasBranchData && !hasGradeData && summary.learnerScore != null
        ? {
            value: summary.learnerScore * 100,
            goal: 100,
            label: i18n.t("learner_score"),
            unit: "%",
            subLabel: i18n.t("score"),
          }
        : null;
    return (
      <PerfOverview
        overallTitle={i18n.t("overall_scores")}
        rings={rings}
        goalRing={goalRing}
        chartTitle={i18n.t(chartTitleKey)}
        groups={groups}
        series={series}
        laneEmptyText={i18n.t("no_chart_data_yet")}
        ringEmptyText={i18n.t("no_chart_data_yet")}
      />
    );
  };

  /**
   * The deneme Tahmini puan card — kept apart from the % overview above it. The circle is the most
   * recent scored deneme out of 500, and the lanes each scored deneme's primary score on 100–500.
   * Rendered only while Deneme is in the Kaynak selection and at least one deneme has a score.
   */
  const renderMockExamScoreCard = (mockRowList: HolisticReportRow[]) => {
    const mockItemList = mockRowList.map((row) => row.item);
    const scoredItemList = mockItemList
      .slice(0, 10)
      .filter((item) => primaryEstimatedScore(item) != null);
    if (scoredItemList.length === 0) return null;
    const latestScored = latestScoredDeneme<any>(mockItemList);
    const groups = scoredItemList.map((item) => {
      const primary = primaryEstimatedScore(item)!;
      const typeText =
        i18n.t(`score_type_${primary.scoreType}`) +
        (primary.partial ? ` · ${i18n.t("estimated_score_partial")}` : "");
      // The type leads the label: the lane truncates long deneme names, and "which score" is the part
      // that must survive the cut.
      return { label: `${typeText} — ${item.name}`, values: { student: primary.value } };
    });
    return (
      <div className="qcw-score-card">
        <p className="qcw-score-card-hint">{i18n.t("mock_exam_score_card_hint")}</p>
        <PerfOverview
          overallTitle={i18n.t("mock_exam_latest_score")}
          rings={[]}
          goalRing={
            latestScored
              ? {
                  value: latestScored.score.value,
                  goal: ESTIMATED_SCORE_MAX,
                  label: latestScored.row.name,
                  unit: "",
                  valueText: latestScored.score.valueText,
                  subLabel: i18n.t("mock_exam_score_out_of", {
                    max: ESTIMATED_SCORE_MAX,
                    type: i18n.t(`score_type_${latestScored.score.scoreType}`),
                  }),
                }
              : null
          }
          chartTitle={i18n.t("overall_mock_exam_scores")}
          groups={groups}
          series={[
            { key: "student", label: i18n.t("mock_exam_estimated_score"), color: C_STUDENT },
          ]}
          laneEmptyText={i18n.t("no_chart_data_yet")}
          ringEmptyText={i18n.t("no_chart_data_yet")}
          laneScale={{
            min: ESTIMATED_SCORE_MIN,
            max: ESTIMATED_SCORE_MAX,
            ticks: [100, 200, 300, 400, 500],
            format: formatEstimatedScoreValue,
          }}
        />
      </div>
    );
  };

  const renderLoading = () => (
    <BaseLoading size="large" primaryColor="#57C2D3" secondaryColor="#57C2D3" />
  );

  /**
   * Ödevler — Şube, Destek and Bireysel TOGETHER. The Kaynak row picks which of the three; the rings,
   * the lanes and the list are all built from the same merged rows, each row naming its kind and
   * opening its own detail.
   */
  const RenderAssignmentsContent = () => {
    if (
      branchAssignmentPerformanceLoading ||
      personalizedAssignmentPerformanceLoading ||
      learnerSupportAssignmentPerformanceLoading ||
      branchAssignmentReportDetailListLoading
    ) {
      return renderLoading();
    }
    const rowList = buildAssignmentReportRowList(
      {
        branchAssignmentItems: branchAssignmentPerformance,
        supportAssignmentItems: learnerSupportAssignmentPerformance,
        personalizedAssignmentItems: personalizedAssignmentPerformance,
      },
      { coterieType: selectedCoterieType, sourceIds: visibleSourceIds },
    );
    const totalPages = Math.ceil(rowList.length / ITEMS_PER_PAGE);
    const safePage = Math.min(assignmentsPageNumber, Math.max(0, totalPages - 1));
    const pageRowList = rowList.slice(
      safePage * ITEMS_PER_PAGE,
      (safePage + 1) * ITEMS_PER_PAGE,
    );
    return (
      <div className="qcw-content-col qcw-stack">
        <style>{perfChartsStyles}</style>
        {renderSourceFilterRow()}
        {rowList.length === 0 ? (
          <EmptyState compact icon="chartBar" title={i18n.t("no_assignment_performance_data")} />
        ) : (
          <>
            {renderRowsOverview(rowList, "overall_assignment_scores")}
            <div className="rp-list">
              <div className="rp-list__row rp-list__row--head">
                <span className="rp-lc">{i18n.t("name")}</span>
                <span className="rp-lc">{i18n.t("begin")}</span>
                <span className="rp-lc">{i18n.t("end")}</span>
                <span className="rp-lc">{i18n.t("status")}</span>
                <span className="rp-lc">{i18n.t("success_percentage")}</span>
                <span className="rp-lc rp-lc--c">{i18n.t("detail")}</span>
                <span className="rp-lc rp-lc--c">{i18n.t("feedback")}</span>
              </div>
              {pageRowList.map((row, index) => (
                <Fragment key={row.key}>
                  <div className="rp-list__row">
                    {renderRowName(row, safePage * ITEMS_PER_PAGE + index)}
                    <span className="rp-lc">
                      {row.beginAt ? renderRpDate(row.beginAt) : <span className="rp-dash">—</span>}
                    </span>
                    <span className="rp-lc">
                      {row.endAt ? renderRpDate(row.endAt) : <span className="rp-dash">—</span>}
                    </span>
                    <span className="rp-lc">{renderRowStatus(row)}</span>
                    <span className="rp-lc rp-lc--prog">{renderRowScore(row)}</span>
                    <span className="rp-lc rp-lc--c">{renderRowDetail(row)}</span>
                    <span className="rp-lc rp-lc--c">{renderRowFeedback(row)}</span>
                  </div>
                  {renderRowFeedbackPanel(row)}
                </Fragment>
              ))}
            </div>
            {renderPager(safePage, setAssignmentsPageNumber, totalPages)}
          </>
        )}
      </div>
    );
  };

  /**
   * Sınavlar — Yazılı and Deneme together. Başarı % is combined question-weighted; the deneme's
   * Tahmini puan keeps its own card and its own column. Yazılı follows the Ders picker; a deneme spans
   * every ders it tested, so it is listed whole whatever the ders (as before).
   */
  const RenderExamsContent = () => {
    if (learnerHandwrittenExamPerformanceLoading || learnerMockExamPerformanceLoading) {
      return renderLoading();
    }
    const rowList = buildExamReportRowList(
      {
        handwrittenExamItems: learnerHandwrittenExamPerformance,
        mockExamItems: learnerMockExamPerformance,
      },
      { coterieType: selectedCoterieType, sourceIds: visibleSourceIds },
    );
    const showEstimatedScore = visibleSourceIds.includes(MOCK_EXAM);
    const mockRowList = rowList.filter((row) => row.kind === MOCK_EXAM);
    const rowClass = showEstimatedScore
      ? "rp-list__row--exam-score"
      : "rp-list__row--exam-detail";
    const totalPages = Math.ceil(rowList.length / ITEMS_PER_PAGE);
    const safePage = Math.min(examsPageNumber, Math.max(0, totalPages - 1));
    const pageRowList = rowList.slice(
      safePage * ITEMS_PER_PAGE,
      (safePage + 1) * ITEMS_PER_PAGE,
    );
    return (
      <div className="qcw-content-col qcw-stack">
        <style>{perfChartsStyles}</style>
        {renderSourceFilterRow()}
        {rowList.length === 0 ? (
          <EmptyState compact icon="chartBar" title={i18n.t("no_exam_performance_data")} />
        ) : (
          <>
            {renderRowsOverview(rowList, "overall_exam_scores")}
            {showEstimatedScore ? renderMockExamScoreCard(mockRowList) : null}
            <div className="rp-list">
              <div className={`rp-list__row ${rowClass} rp-list__row--head`}>
                <span className="rp-lc">{i18n.t("name")}</span>
                <span className="rp-lc">{i18n.t("date")}</span>
                <span className="rp-lc">{i18n.t("status")}</span>
                <span className="rp-lc">{i18n.t("success_percentage")}</span>
                {showEstimatedScore ? (
                  <span className="rp-lc">{i18n.t("mock_exam_score_column")}</span>
                ) : null}
                <span className="rp-lc rp-lc--c">{i18n.t("detail")}</span>
              </div>
              {pageRowList.map((row, index) => (
                <div className={`rp-list__row ${rowClass}`} key={row.key}>
                  {renderRowName(row, safePage * ITEMS_PER_PAGE + index)}
                  <span className="rp-lc">
                    {row.sortAt ? renderRpDate(row.sortAt) : <span className="rp-dash">—</span>}
                  </span>
                  <span className="rp-lc">{renderRowStatus(row)}</span>
                  <span className="rp-lc rp-lc--prog">{renderRowScore(row)}</span>
                  {showEstimatedScore ? (
                    <span className="rp-lc">
                      {row.kind === MOCK_EXAM ? (
                        renderEstimatedScoreCell(row.item)
                      ) : (
                        <span className="rp-dash">—</span>
                      )}
                    </span>
                  ) : null}
                  <span className="rp-lc rp-lc--c">{renderRowDetail(row)}</span>
                </div>
              ))}
            </div>
            {renderPager(safePage, setExamsPageNumber, totalPages)}
          </>
        )}
      </div>
    );
  };

  if (isMinimized) {
    return RenderMinimizedGraphicalData();
  } else {
    const isStrongWeak = selectedPerformanceType === "STRONG_WEAK";
    const isGeneralStatus = selectedPerformanceType === "GENERAL_STATUS";
    const isSkills = selectedPerformanceType === "SKILLS";
    const isAssignments = selectedPerformanceType === "ASSIGNMENTS";
    const isExams = selectedPerformanceType === "EXAMS";
    return (
      <>
        <style>{reportCardViewStyles}</style>
        {RenderIndividualModelModal()}
        <SubjectDescriptionNavigationModal
          target={subjectDescriptionTarget}
          coterieType={selectedCoterieType}
          onClose={() => setSubjectDescriptionTarget(null)}
        />
        <div className="qcw-main-root">
          {/* Sınavlar is exempt: a deneme is listed whole whatever the ders, so a learner whose only
              evidence is a deneme still reaches it. */}
          {selectedCoterieType === "DEFAULT_OPTION" && !isExams ? (
            <EmptyState
              icon="chartBar"
              title={i18n.t("reports_select_lesson_title")}
            />
          ) : isSkills ? (
            RenderSkillsContent()
          ) : isStrongWeak ? (
            RenderStrongWeakContent()
          ) : isGeneralStatus ? (
            RenderGeneralStatusContent()
          ) : isAssignments ? (
            RenderAssignmentsContent()
          ) : isExams ? (
            RenderExamsContent()
          ) : null}
        </div>
      </>
    );
  }
};

export default ReportCardView;
